import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import Switch from "react-switch";
import "./rules.css";
import { fetchThemeData } from "../../themeSlice";
import { processQuestions } from "../../questionSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import { updateThemeData } from "../../functions/updateThemeData";

const THANK_YOU_MSG_MAX_CHARS = 200;

/** Category order from theme payload (themeset + DB merge from fetchThemeData). */
function getThankyouCategoryKeys(themeData) {
  if (!themeData || typeof themeData !== "object") return [];
  const d = themeData.default_cat;
  const f = themeData.full_default_cat;
  if (Array.isArray(d) && d.length > 0) return d.map(String);
  if (Array.isArray(f) && f.length > 0) return f.map(String);
  const tm = themeData.thankyou_message;
  if (tm && typeof tm === "object" && !Array.isArray(tm)) {
    return Object.keys(tm);
  }
  return [];
}

/** Themeset + API only use plain strings per category (no score-band objects). */
function migrateThankyouMessageToCategoryStrings(raw, categoryKeys) {
  const o = Object.fromEntries(categoryKeys.map((k) => [k, ""]));
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return o;
  for (const k of categoryKeys) {
    if (typeof raw[k] === "string") o[k] = raw[k];
  }
  return o;
}

function clampThankyouCategoryStrings(msg, categoryKeys, maxLen) {
  const o = { ...msg };
  for (const k of categoryKeys) {
    if (typeof o[k] === "string" && o[k].length > maxLen) o[k] = o[k].slice(0, maxLen);
  }
  return o;
}

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, status, data } = useSelector((state) => state.theme);

  const [customTextThankYouPage, setCustomTextThankYouPage] = useState("");
  const [rules, setRules] = useState([""]);
  const [thankyouToggle, setThankyouToggle] = useState(false);
  const [thankyouMessage, setThankyouMessage] = useState({});
  const [boxTextColor, setBoxTextColor] = useState("#000000");
  const [boxBgColor, setBoxBgColor] = useState("#ffffff");
  const [textColor, setTextColor] = useState("#000000");
  const [popupColor, setPopupColor] = useState("#ffffff");
  const [correctPoints, setCorrectPoints] = useState("1");
  const [wrongPoints, setWrongPoints] = useState("1");

  useEffect(() => {
    dispatch(processQuestions());
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    const categoryKeys = getThankyouCategoryKeys(data);
    setCustomTextThankYouPage(
      data.custom_text_thank_you_page != null ? String(data.custom_text_thank_you_page) : ""
    );
    setRules(Array.isArray(data.rules) && data.rules.length ? data.rules : [""]);
    setThankyouToggle(data.thankyou_toggle === "true" || data.thankyou_toggle === true);
    setThankyouMessage(
      clampThankyouCategoryStrings(
        migrateThankyouMessageToCategoryStrings(data.thankyou_message, categoryKeys),
        categoryKeys,
        THANK_YOU_MSG_MAX_CHARS
      )
    );
    setBoxTextColor(data.box_text_color ?? "#000000");
    setBoxBgColor(data.box_bg_color ?? "#ffffff");
    setTextColor(data.text_color ?? "#000000");
    setPopupColor(data.popup_color ?? data.box_bg_color ?? "#ffffff");
    setCorrectPoints(String(data.correct_points ?? "1"));
    setWrongPoints(String(data.wrong_points ?? "1"));
  }, [data]);

  const categoryKeys = data ? getThankyouCategoryKeys(data) : [];

  const handleThankyouCategoryChange = (cat, val) => {
    if (val.length > THANK_YOU_MSG_MAX_CHARS) {
      void Swal.fire({
        icon: "warning",
        title: "Max limit crossed",
        text: `Thank you messages can be at most ${THANK_YOU_MSG_MAX_CHARS} characters each.`,
      });
      val = val.slice(0, THANK_YOU_MSG_MAX_CHARS);
    }
    setThankyouMessage((prev) => ({ ...prev, [cat]: val }));
  };

  const handleRuleChange = (idx, val) => {
    if (val.length > 200) {
      Swal.fire("Limit Exceeded", "Rules can be at most 200 characters.", "warning");
      val = val.slice(0, 200);
    }
    const arr = [...rules];
    arr[idx] = val;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= 6) {
      return Swal.fire("Limit Reached", "You can only add up to 6 rules.", "warning");
    }
    setRules([...rules, ""]);
  };

  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      return Swal.fire("At Least One Rule Required", "You must have at least one rule.", "warning");
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }

    const filledRules = rules.map((r) => r.trim()).filter(Boolean);
    if (filledRules.length === 0) {
      Swal.fire({
        title: "Validation",
        text: "Add at least one game rule. Rules cannot be left blank.",
        icon: "error",
      });
      return;
    }

    const msgOut = { ...thankyouMessage };
    for (const k of categoryKeys) {
      if (!(k in msgOut)) msgOut[k] = "";
    }

    const payload = {
      data: {
        custom_text_thank_you_page: customTextThankYouPage,
        rules: filledRules,
        thankyou_toggle: thankyouToggle ? "true" : "false",
        thankyou_message: msgOut,
        box_text_color: boxTextColor,
        box_bg_color: boxBgColor,
        text_color: textColor,
        popup_color: popupColor,
        correct_points: String(correctPoints),
        wrong_points: String(wrongPoints),
      },
      organizationId: organizationId || "admin",
      sessionId: sessionId || "admin",
      currentTheme,
    };

    try {
      await updateThemeData({ payload, token: adminToken });
      Swal.fire("Saved", "Theme settings saved successfully.", "success");
      navigate("/admin/themeupdate");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save. Please try again.", "error");
    }
  };

  return (
    <>
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>
      <div className="rules-page-new">
        <div className="admin-tabs-holder">
          <div className="rules-page-columns">
            <div className="rules-page-left">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-pen"></i> &nbsp; Thank you
                </div>
                <div className="form-group-v1">
                  <label>Custom text (thank you page)</label>
                  <input
                    type="text"
                    value={customTextThankYouPage}
                    onChange={(e) => setCustomTextThankYouPage(e.target.value.slice(0, 200))}
                    className="form-control"
                    maxLength={200}
                  />
                  <div className="code" style={{ marginTop: "6px" }}>
                    Max {THANK_YOU_MSG_MAX_CHARS} characters.
                  </div>
                </div>
                <div className="form-group-v1 toogle-row">
                  <label>Thank you message (per category)</label>
                  <Switch checked={thankyouToggle} onChange={setThankyouToggle} />
                </div>
                <div className="code" style={{ marginTop: "-8px", marginBottom: "10px" }}>
                  Max {THANK_YOU_MSG_MAX_CHARS} characters per category message.
                </div>
                {thankyouToggle && categoryKeys.length > 0 && (
                  <>
                    {categoryKeys.map((cat) => (
                      <div key={cat} className="form-group-v1">
                        <label>{cat}</label>
                        <textarea
                          rows={3}
                          value={thankyouMessage[cat] ?? ""}
                          onChange={(e) => handleThankyouCategoryChange(cat, e.target.value)}
                          className="form-control rules-textarea"
                          maxLength={THANK_YOU_MSG_MAX_CHARS}
                        />
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>

            <div className="rules-page-center">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-trophy"></i> &nbsp; Game rules
                </div>
                <h3 className="custom-rules-title">Rules</h3>
                {rules.map((rule, i) => (
                  <div className="rule-row" key={i}>
                    <span className="rule-number">{i + 1}.</span>
                    <textarea
                      className="rule-input rule-textarea"
                      placeholder="Enter rule"
                      value={rule}
                      onChange={(e) => handleRuleChange(i, e.target.value)}
                      rows={3}
                    />
                    <button className="remove-btn" onClick={() => handleRemoveRule(i)}>
                      &times;
                    </button>
                  </div>
                ))}
                <div className="rules-button-container">
                  <button className="add-btn" onClick={handleAddRule}>
                    + Add New Rule
                  </button>
                  <div className="code">
                    Max 6 rules. At least 1 required. 200 characters per rule.
                  </div>
                </div>
              </div>
            </div>

            <div className="rules-page-right">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-palette"></i> &nbsp; Colors &amp; points
                </div>
                <div className="pallet-holder">
                  {[
                    { key: "text", label: "Text color", value: textColor, set: setTextColor },
                    { key: "box_bg", label: "Submit / Continue button background", value: boxBgColor, set: setBoxBgColor },
                    { key: "box_text", label: "Submit / Continue button text", value: boxTextColor, set: setBoxTextColor },
                    { key: "popup_bg", label: "Popup / Modal background", value: popupColor, set: setPopupColor },
                  ].map(({ key, label, value, set }) => (
                    <div className="pallet-row pallet-row--label-left" key={key}>
                      <label>{label}</label>
                      <input
                        type="color"
                        className="pallet-swatch"
                        value={value || "#000000"}
                        onChange={(e) => set(e.target.value)}
                      />
                    </div>
                  ))}
                  <div className="pallet-row pallet-row--label-left">
                    <label>Correct answer points</label>
                    <input
                      type="text"
                      className="pallet-row-input"
                      value={correctPoints}
                      onChange={(e) => setCorrectPoints(e.target.value.slice(0, 16))}
                    />
                  </div>
                  <div className="pallet-row pallet-row--label-left">
                    <label>Wrong answer points</label>
                    <input
                      type="text"
                      className="pallet-row-input"
                      value={wrongPoints}
                      onChange={(e) => setWrongPoints(e.target.value.slice(0, 16))}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="rules-action-holder">
            <button className="save-and-continue" onClick={handleSubmit} disabled={status === "loading"}>
              Save
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Rules;
